import React, { useState, useMemo } from 'react';
import { ProductGroup } from '../types';
import Pagination from './Pagination';
import { ITEMS_PER_PAGE, PencilIcon, TrashIcon, EyeIcon, DocumentTextIcon, CubeIcon } from '../constants';

interface GroupTableProps {
  groups: ProductGroup[];
  onModify: (group: ProductGroup) => void;
  onDelete: (group: ProductGroup) => void;
}

const GroupTable: React.FC<GroupTableProps> = ({ groups, onModify, onDelete }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState<'card' | 'table'>('card');

  const totalPages = Math.ceil(groups.length / ITEMS_PER_PAGE);

  const currentTableData = useMemo(() => {
    const firstPageIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const lastPageIndex = firstPageIndex + ITEMS_PER_PAGE;
    return groups.slice(firstPageIndex, lastPageIndex);
  }, [currentPage, groups]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  if (groups.length === 0) {
    return (
      <div className="bg-white p-8 rounded-xl shadow-lg text-center">
        <div className="text-6xl mb-4">📦</div>
        <p className="text-slate-600 text-lg mb-2">No product groups found</p>
        <p className="text-slate-500 text-sm">Click "Add Group" to create your first product group</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* View Mode Toggle */}
      <div className="flex justify-end">
        <div className="inline-flex rounded-md shadow-sm" role="group">
          <button
            type="button"
            onClick={() => setViewMode('card')}
            className={`px-4 py-2 text-sm font-medium rounded-l-lg border ${
              viewMode === 'card'
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
            }`}
          >
            Card View
          </button>
          <button
            type="button"
            onClick={() => setViewMode('table')}
            className={`px-4 py-2 text-sm font-medium rounded-r-lg border-t border-b border-r ${
              viewMode === 'table'
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
            }`}
          >
            Table View
          </button>
        </div>
      </div>

      {viewMode === 'card' ? (
        // Card View
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {currentTableData.map((group) => (
            <div key={group.id} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-200">
              {/* Card Header */}
              <div className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white p-4 rounded-t-xl">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold">{group.groupName}</h3>
                    <p className="text-sm opacity-90 mt-1">ID: {group.groupId}</p>
                  </div>
                  <div className="flex gap-2">
                    {(group.showOnDashboard === undefined || group.showOnDashboard) && (
                      <div className="bg-white/20 p-1.5 rounded" title="Visible on Dashboard">
                        <EyeIcon className="w-4 h-4" />
                      </div>
                    )}
                    {(group.showOnReports === undefined || group.showOnReports) && (
                      <div className="bg-white/20 p-1.5 rounded" title="Visible on Reports">
                        <DocumentTextIcon className="w-4 h-4" />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Card Body */}
              <div className="p-4 space-y-3">
                {/* Infeed Configuration */}
                <div className="flex items-start space-x-3">
                  <span className="text-xl mt-0.5">📥</span>
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Infeed</p>
                    <p className="text-sm text-slate-800">{group.infeedDisplayName || 'Not configured'}</p>
                    {group.infeedBatchWeight && (
                      <p className="text-xs text-slate-500 mt-0.5">Batch: {group.infeedBatchWeight} lbs</p>
                    )}
                  </div>
                </div>

                {/* Pocket Configuration */}
                <div className="flex items-start space-x-3">
                  <span className="text-xl mt-0.5">🎯</span>
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Pockets</p>
                    {group.pocketAssignments && group.pocketAssignments.length > 0 ? (
                      <div>
                        <p className="text-sm text-slate-800">
                          {group.pocketAssignments.length} pocket{group.pocketAssignments.length !== 1 ? 's' : ''} configured
                        </p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {group.pocketAssignments.sort((a, b) => a.pocketNumber - b.pocketNumber).map(pocket => (
                            <span key={pocket.pocketNumber} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                              P{pocket.pocketNumber}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400">No pockets assigned</p>
                    )}
                  </div>
                </div>

                {/* Outfeed Configuration */}
                <div className="flex items-start space-x-3">
                  <span className="text-xl mt-0.5">📤</span>
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Outfeed</p>
                    {group.outfeedProductIds && group.outfeedProductIds.length > 0 ? (
                      <div>
                        <p className="text-sm text-slate-800">
                          {group.outfeedProductIds.length} product{group.outfeedProductIds.length !== 1 ? 's' : ''}
                        </p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {group.showSummedOutfeed && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              Summed: {group.summedOutfeedName || 'Outfeed'}
                            </span>
                          )}
                          {group.individualOutfeedProductIds && group.individualOutfeedProductIds.length > 0 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                              {group.individualOutfeedProductIds.length} Individual
                            </span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400">Not configured</p>
                    )}
                  </div>
                </div>

                {/* Yield Configuration */}
                <div className="flex items-start space-x-3">
                  <span className="text-xl mt-0.5">📈</span>
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Yield</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {group.showTotalYield && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                          Total
                        </span>
                      )}
                      {group.individualYieldProductIds && group.individualYieldProductIds.length > 0 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                          {group.individualYieldProductIds.length} Individual
                        </span>
                      )}
                      {!group.showTotalYield && (!group.individualYieldProductIds || group.individualYieldProductIds.length === 0) && (
                        <p className="text-sm text-slate-400">Not configured</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Metrics */}
                <div className="pt-2 border-t border-slate-200">
                  <div className="flex items-start space-x-2">
                    <span className="text-lg">📊</span>
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">Metrics</p>
                      <div className="flex flex-wrap gap-1">
                        {group.dashboardMetrics && Array.isArray(group.dashboardMetrics) && group.dashboardMetrics.length > 0 ? (
                          group.dashboardMetrics
                            .filter((metric: any) => metric.enabled)
                            .map((metric: any, index: number) => (
                              <span
                                key={metric.id || index}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800"
                              >
                                {metric.displayName || metric.type}
                              </span>
                            ))
                        ) : (
                          <span className="text-sm text-slate-400">No metrics</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card Footer */}
              <div className="bg-slate-50 px-4 py-3 rounded-b-xl flex justify-end space-x-2">
                <button
                  onClick={() => onModify(group)}
                  className="px-3 py-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-100 rounded-lg border border-indigo-200 hover:border-indigo-300 transition-all"
                  title="Modify Group"
                >
                  <PencilIcon className="w-4 h-4 inline mr-1" />
                  Edit
                </button>
                <button
                  onClick={() => onDelete(group)}
                  className="px-3 py-1.5 text-sm font-medium text-red-500 hover:text-red-700 hover:bg-red-100 rounded-lg border border-red-200 hover:border-red-300 transition-all"
                  title="Delete Group"
                >
                  <TrashIcon className="w-4 h-4 inline mr-1" />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        // Table View (simplified version of original)
        <div className="bg-white p-4 md:p-6 rounded-xl shadow-lg">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px] text-sm text-left text-slate-600">
              <thead className="text-xs text-slate-500 uppercase bg-slate-50">
                <tr>
                  <th scope="col" className="px-4 py-3">Group</th>
                  <th scope="col" className="px-4 py-3">Configuration</th>
                  <th scope="col" className="px-4 py-3 text-center">Metrics</th>
                  <th scope="col" className="px-4 py-3 text-center">Visibility</th>
                  <th scope="col" className="px-4 py-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentTableData.map((group) => (
                  <tr key={group.id} className="bg-white border-b border-slate-200 hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-slate-900">{group.groupName}</p>
                        <p className="text-xs text-slate-500">ID: {group.groupId}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="space-y-1">
                        <div className="text-xs">
                          <span className="font-semibold">Infeed:</span> {group.infeedDisplayName || 'Not set'}
                          {group.infeedBatchWeight && <span className="text-slate-500 ml-1">({group.infeedBatchWeight} lbs)</span>}
                        </div>
                        <div className="text-xs">
                          <span className="font-semibold">Pockets:</span> {group.pocketAssignments?.length || 0}
                          {group.pocketAssignments && group.pocketAssignments.length > 0 && (
                            <span className="text-slate-500 ml-1">
                              [{group.pocketAssignments.map(p => p.pocketNumber).sort((a, b) => a - b).join(', ')}]
                            </span>
                          )}
                        </div>
                        <div className="text-xs">
                          <span className="font-semibold">Outfeed:</span> {group.outfeedProductIds?.length || 0} products
                          {group.showSummedOutfeed && <span className="text-slate-500 ml-1">(Summed)</span>}
                          {group.showIndividualOutfeed && <span className="text-slate-500 ml-1">(Individual)</span>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex flex-wrap gap-1 justify-center">
                        {group.showInfeedPph && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                            I-PPH
                          </span>
                        )}
                        {group.showInfeedPpm && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                            I-PPM
                          </span>
                        )}
                        {group.showOutfeedPph && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                            O-PPH
                          </span>
                        )}
                        {group.showOutfeedPpm && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                            O-PPM
                          </span>
                        )}
                        {group.showTotalYield && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                            Yield
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex justify-center gap-2">
                        {(group.showOnDashboard === undefined || group.showOnDashboard) && (
                          <EyeIcon className="w-5 h-5 text-blue-600" title="Dashboard" />
                        )}
                        {(group.showOnReports === undefined || group.showOnReports) && (
                          <DocumentTextIcon className="w-5 h-5 text-green-600" title="Reports" />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex justify-center space-x-2">
                        <button
                          onClick={() => onModify(group)}
                          className="p-2 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-100 rounded-lg transition-all"
                          title="Modify Group"
                        >
                          <PencilIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => onDelete(group)}
                          className="p-2 text-red-500 hover:text-red-700 hover:bg-red-100 rounded-lg transition-all"
                          title="Delete Group"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};

export default GroupTable;